import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

/** One property's rent for one DUE month, summed off the owner rent ledger. */
export type OwnerRentMonth = {
  dueCents: number;
  paidCents: number;
  openCents: number;
  openTenants: number;
  overdueCents: number;
};

/** First day of the current month as YYYY-MM-01, from the local date parts. */
export function currentMonthStart(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

/**
 * The same ledger `owner_portfolio_dashboard` reads (`owner_rent_rows`,
 * 20261021195000), narrowed to one property and one due month, so the property
 * page and the analytics page cannot disagree about what is due, paid or open.
 * Scoped to the caller's own units on the server.
 */
export function useOwnerRentLedger(
  propertyId: number | null,
  month: string = currentMonthStart()
) {
  return useQuery({
    queryKey: ["owner-rent-ledger", propertyId, month],
    enabled: !!propertyId,
    queryFn: async (): Promise<OwnerRentMonth> => {
      const { data, error } = await supabase.rpc("owner_rent_ledger", {
        _from: month,
        _to: month,
        _property_id: propertyId as number,
      });
      if (error) throw error;
      const total: OwnerRentMonth = {
        dueCents: 0,
        paidCents: 0,
        openCents: 0,
        openTenants: 0,
        overdueCents: 0,
      };
      // One row per owned unit; a tenant belongs to exactly one unit, so the
      // per-unit tenant counts add up without double counting.
      for (const row of data ?? []) {
        total.dueCents += Number(row.due_cents);
        total.paidCents += Number(row.paid_cents);
        total.openCents += Number(row.open_cents);
        total.openTenants += Number(row.open_tenants);
        total.overdueCents += Number(row.overdue_cents);
      }
      return total;
    },
  });
}
